// import React, { createContext, useContext, useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";

// const AuthContext = createContext();

// const API_BASE = process.env.REACT_APP_USER_SERVICE_URL;

// export const AuthProvider = ({ children }) => {
//   const navigate = useNavigate();

//   const [auth, setAuth] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [loggingOut, setLoggingOut] = useState(false);

//   useEffect(() => {
//     const token = localStorage.getItem("authToken");
//     const userId = localStorage.getItem("authUserId");
//     const email = localStorage.getItem("authEmail");
//     const phone = localStorage.getItem("authPhone");
//     const firstName = localStorage.getItem("authFirstName");
//     const roles = localStorage.getItem("authRoles");

//     if (token && userId) {
//       setAuth({
//         userId,
//         email,
//         phone,
//         firstName,
//         token,
//         roles: roles ? roles.split(",") : [],
//       });
//     }

//     setLoading(false);
//   }, []);

//   /* ---------------- LOGIN ---------------- */
//   const login = (authData) => {
//     localStorage.setItem("authToken", authData.token);
//     localStorage.setItem("authUserId", authData.userId);
//     localStorage.setItem("authEmail", authData.email);
//     localStorage.setItem("authPhone", authData.phone);
//     localStorage.setItem("authFirstName", authData.firstName);
//     localStorage.setItem("authRoles", authData.roles.join(","));

//     setAuth(authData);
//   };

//   /* ---------------- LOGOUT (GLOBAL) ---------------- */
//   const logout = async (callBackend = true) => {
//     if (loggingOut) return;

//     setLoggingOut(true);

//     const token = localStorage.getItem("authToken");
//     let backendMessage = "";

//     // notify backend (blacklist JWT)
//     if (callBackend && token) {
//       try {
//         const response = await fetch(`${API_BASE}/auth/logout`, {
//           method: "POST",
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         });
//         if (response.ok) {
//           const data = await response.json();
//           backendMessage = data?.message || "";
//         }
//       } catch {
//         backendMessage =
//           "Logout failed on server, but you are logged out locally.";
//       }
//     }

//     // clear only auth keys
//     localStorage.removeItem("authToken");
//     localStorage.removeItem("authUserId");
//     localStorage.removeItem("authEmail");
//     localStorage.removeItem("authPhone");
//     localStorage.removeItem("authFirstName");
//     localStorage.removeItem("authRoles");

//     setAuth(null);
//     setLoggingOut(false);

//     console.log(backendMessage);

//     // Redirect to login page for clarity
//     navigate("/login", { replace: true });
//   };

//   /* ---------------- AUTH FETCH (401 AUTO LOGOUT) ---------------- */
//   const authFetch = async (endpoint, options = {}) => {
//     const token = localStorage.getItem("authToken");

//     const response = await fetch(`${API_BASE}${endpoint}`, {
//       ...options,
//       headers: {
//         ...(options.headers || {}),
//         Authorization: token ? `Bearer ${token}` : "",
//         "Content-Type": "application/json",
//       },
//     });

//     if (response.status === 401) {
//       await logout(false); // token already invalid
//       throw new Error("Unauthorized");
//     }

//     return response;
//   };

//   const isAuthenticated = !!auth?.token;

//   return (
//     <AuthContext.Provider
//       value={{
//         auth,
//         isAuthenticated,
//         login,
//         logout,
//         authFetch,
//         loading,
//         loggingOut,
//       }}
//     >
//       {children}
//     </AuthContext.Provider>
//   );
// };

// export const useAuth = () => useContext(AuthContext);

import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext();
const API_BASE = process.env.REACT_APP_USER_SERVICE_URL;

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [auth, setAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);

  // ------------------- Decode JWT -------------------
  const decodeJWT = (token) => {
    try {
      const payload = token.split(".")[1];
      return JSON.parse(atob(payload));
    } catch (err) {
      console.error("Invalid JWT:", err);
      return null;
    }
  };

  // ------------------- Init from localStorage -------------------
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      const decoded = decodeJWT(token);

      if (decoded) {
        setAuth({
          userId: decoded.userId,
          email: decoded.sub,
          phone: decoded.phone || "",
          firstName: decoded.fullName?.split(" ")[0] || "",
          token,
          roles: decoded.role ? [decoded.role] : [],
        });
      } else {
        localStorage.removeItem("authToken");
      }
    }
    setLoading(false);
  }, []);

  // ------------------- Login -------------------
  const login = (authData) => {
    if (!authData.token) throw new Error("Missing token in login data");

    const decoded = decodeJWT(authData.token);
    if (!decoded) throw new Error("Invalid JWT token");

    const roles = decoded.role ? [decoded.role] : [];

    const user = {
      userId: decoded.userId,
      email: decoded.sub,
      phone: decoded.phone || "",
      firstName: decoded.fullName?.split(" ")[0] || "",
      token: authData.token,
      roles,
    };

    // Save in localStorage
    localStorage.setItem("authToken", authData.token);
    localStorage.setItem("authUserId", user.userId);
    localStorage.setItem("authEmail", user.email);
    localStorage.setItem("authPhone", user.phone);
    localStorage.setItem("authFirstName", user.firstName);
    localStorage.setItem("authRoles", roles.join(","));

    setAuth(user);
  };

  // ------------------- Logout -------------------
  const logout = async (callBackend = true) => {
    if (loggingOut) return;
    setLoggingOut(true);

    const token = localStorage.getItem("authToken");
    let backendMessage = "";

    if (callBackend && token) {
      try {
        const res = await fetch(`${API_BASE}/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });

        // Only parse JSON if response has body
        const text = await res.text();
        if (text) {
          const data = JSON.parse(text);
          backendMessage = data?.message || "";
        }
      } catch {
        backendMessage =
          "Logout failed on server, but you are logged out locally.";
      }
    }

    // Clear localStorage
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUserId");
    localStorage.removeItem("authEmail");
    localStorage.removeItem("authPhone");
    localStorage.removeItem("authFirstName");
    localStorage.removeItem("authRoles");

    setAuth(null);
    setLoggingOut(false);
    console.log(backendMessage);

    navigate("/login", { replace: true });
  };

  // ------------------- Auth Fetch -------------------
  const authFetch = async (endpoint, options = {}) => {
    const token = localStorage.getItem("authToken");
    const res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    });

    if (res.status === 401) {
      await logout(false);
      throw new Error("Unauthorized");
    }

    return res;
  };

  const isAuthenticated = !!auth?.token;

  return (
    <AuthContext.Provider
      value={{
        auth,
        isAuthenticated,
        login,
        logout,
        authFetch,
        loading,
        loggingOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
